import { useState } from "react";
import { Select, Empty, InputNumber, Tooltip } from "antd";
import {
  ThunderboltOutlined,
  EyeOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import LoadableComp from "../commonComponents/LoadableComp/LoadableComp";
import MondayButton from "../commonComponents/MondayButton/MondayButton";
import useAquamanAdmin from "./hooks/useAquamanAdmin";
import RosterPanel from "./components/RosterPanel";
import WeekTimeline from "./components/WeekTimeline";
import ReassignModal from "./components/ReassignModal";
import FairnessPanel from "./components/FairnessPanel";
import HistoryImportPanel from "./components/HistoryImportPanel";
import WarningsBanner from "./components/WarningsBanner";
import AquamanPrintSheet, {
  printAquamanSheet,
} from "./components/AquamanPrintSheet";
import "./Aquaman.scss";

// Admin page for the weekly office water duty ("Aquaman"): one roster per
// branch, a fair rotation that swaps around approved leave, and manual
// overrides for the weeks reality gets in the way of.
const Aquaman = () => {
  const { isDarkMode } = useSelector((state) => state.usersList);
  const {
    rosters,
    roster,
    availableTeams,
    selectedRosterId,
    setSelectedRosterId,
    weeks,
    fairness,
    warnings,
    preview,
    clearPreview,
    loading,
    saving,
    onSaveRoster,
    onCreateRoster,
    onGenerate,
    onWeekAction,
    onImportHistory,
    onAdjustHistory,
  } = useAquamanAdmin();

  const [weekCount, setWeekCount] = useState(12);
  const [modal, setModal] = useState({ open: false, mode: null, week: null });

  const closeModal = () => setModal({ open: false, mode: null, week: null });

  return (
    <LoadableComp loading={loading && rosters.length === 0}>
      <div className={`aquamanPage ${isDarkMode ? "dark" : ""}`}>
        <div className="aquamanHeader">
          <div className="aquamanHeaderLeft">
            <h1>Aquaman</h1>
            <span className="aquamanMuted">
              Rotacioni javor i ndërrimit të ujit në zyrë
            </span>
          </div>
          <div className="aquamanHeaderRight">
            <Select
              style={{ minWidth: 200 }}
              value={selectedRosterId}
              placeholder="Zgjidh degën"
              onChange={(id) => {
                clearPreview();
                setSelectedRosterId(id);
              }}
              options={rosters.map((r) => ({
                value: r.dutyId,
                label: r.teamLabel || r.team,
              }))}
            />
            {availableTeams.length > 0 ? (
              <Select
                style={{ minWidth: 200 }}
                placeholder="Krijo rosterin për degë tjetër"
                value={null}
                onChange={onCreateRoster}
                options={availableTeams.map((t) => ({ value: t, label: t }))}
              />
            ) : null}
            <Tooltip title="Sa javë përpara planifikohen">
              <InputNumber
                min={1}
                max={52}
                value={weekCount}
                onChange={(v) => setWeekCount(v || 1)}
              />
            </Tooltip>
            <MondayButton
              className="mondayButtonBlue"
              Icon={<EyeOutlined />}
              hasIcon
              disabled={saving || !selectedRosterId}
              onClick={() => onGenerate({ weeks: weekCount, dryRun: true })}
            >
              Parashiko
            </MondayButton>
            {preview ? (
              <MondayButton className="mondayButtonRed" onClick={clearPreview}>
                Mbyll parashikimin
              </MondayButton>
            ) : null}
            <MondayButton
              Icon={<ThunderboltOutlined />}
              hasIcon
              spin={saving}
              disabled={saving || !selectedRosterId}
              onClick={() => onGenerate({ weeks: weekCount, dryRun: false })}
            >
              Gjenero
            </MondayButton>
            <Tooltip title="Fletë për muri: 20 javët në vijim në një faqe A4.">
              <MondayButton
                className="mondayButtonBlue"
                Icon={<PrinterOutlined />}
                hasIcon
                disabled={!roster || (weeks || []).length === 0}
                onClick={printAquamanSheet}
              >
                Printo
              </MondayButton>
            </Tooltip>
          </div>
        </div>

        <WarningsBanner warnings={warnings} />

        {!roster ? (
          <Empty description="Nuk ka roster. Zgjidh një degë për të krijuar një." />
        ) : (
          <div className="aquamanGrid">
            <div className="aquamanColumn">
              <RosterPanel {...{ roster, saving, onSaveRoster }} />
              <HistoryImportPanel {...{ saving, onImportHistory }} />
            </div>
            <div className="aquamanColumn">
              <WeekTimeline
                {...{ weeks, preview, saving, onWeekAction }}
                onOpenReassign={(week) =>
                  setModal({ open: true, mode: "reassign", week })
                }
                onOpenSwap={(week) => setModal({ open: true, mode: "swap", week })}
              />
              <FairnessPanel {...{ fairness, saving, onAdjustHistory }} />
            </div>
          </div>
        )}

        <AquamanPrintSheet roster={roster} weeks={weeks} />

        <ReassignModal
          open={modal.open}
          mode={modal.mode}
          week={modal.week}
          roster={roster}
          weeks={weeks}
          saving={saving}
          onCancel={closeModal}
          onSubmit={async (payload) => {
            await onWeekAction(payload, "Java u përditësua.");
            closeModal();
          }}
        />
      </div>
    </LoadableComp>
  );
};

export default Aquaman;
